import { useEffect, useState } from "react";
import { masterApi, apiError, formatBRL } from "@/lib/api";
import { ExcelBtn } from "@/lib/excelDownload";
import { useUnit } from "@/pages/Master/MasterApp";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
         AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { modal } from "@/lib/modal";
import { Lock, RefreshCw, Eye, FileSearch, Trash2, AlertTriangle } from "lucide-react";

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

const fmtAgo = (iso) => {
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h > 0) return `${h}h${m.toString().padStart(2, "0")}`;
  return `${m}min`;
};

export default function MasterCaixa() {
  const { unitId } = useUnit();
  const [items, setItems] = useState([]);
  const [abertos, setAbertos] = useState([]);
  const [frentistas, setFrentistas] = useState([]);
  const [frentistaId, setFrentistaId] = useState("");
  const [inicio, setInicio] = useState("");
  const [fim, setFim] = useState("");
  const [confirmFechar, setConfirmFechar] = useState(null); // caixa aberto a fechar
  const [detalhe, setDetalhe] = useState(null); // { caixa, podeExcluir }

  useEffect(() => {
    masterApi.get("/frentistas").then((r) => setFrentistas(r.data)).catch(() => {});
  }, []);

  const loadAbertos = async () => {
    const params = new URLSearchParams();
    if (unitId && unitId !== "all") params.set("unit_id", unitId);
    try {
      const { data } = await masterApi.get(`/master/caixas-abertos?${params.toString()}`);
      setAbertos(data);
    } catch (e) { modal.error(apiError(e)); }
  };

  const loadFechados = async () => {
    const params = new URLSearchParams();
    if (unitId && unitId !== "all") params.set("unit_id", unitId);
    if (frentistaId) params.set("frentista_id", frentistaId);
    if (inicio) params.set("inicio", new Date(inicio).toISOString());
    if (fim) params.set("fim", new Date(fim + "T23:59:59").toISOString());
    try {
      const { data } = await masterApi.get(`/master/relatorios/caixas?${params.toString()}`);
      setItems(data);
    } catch (e) { modal.error(apiError(e)); }
  };

  useEffect(() => { loadAbertos(); loadFechados(); }, [unitId, frentistaId, inicio, fim]);

  const doFechar = async () => {
    const cx = confirmFechar;
    setConfirmFechar(null);
    try {
      await masterApi.post(`/master/caixas/${cx.id}/fechar`, {});
      modal.success(`Caixa de ${cx.frentista_nome} fechado`);
      await loadAbertos();
      await loadFechados();
    } catch (e) { modal.error(apiError(e)); }
  };

  const consolidated = items.reduce((acc, c) => ({
    bruto: acc.bruto + c.total_bruto, liquido: acc.liquido + c.total_liquido,
    desconto: acc.desconto + c.total_desconto, count: acc.count + c.count,
  }), { bruto: 0, liquido: 0, desconto: 0, count: 0 });

  const consolidadoAbertos = abertos.reduce((acc, c) => ({
    bruto: acc.bruto + c.total_bruto, liquido: acc.liquido + c.total_liquido,
    desconto: acc.desconto + c.total_desconto, count: acc.count + c.count,
  }), { bruto: 0, liquido: 0, desconto: 0, count: 0 });

  const excelQuery = [
    unitId !== "all" ? `unit_id=${unitId}` : "",
    frentistaId ? `frentista_id=${frentistaId}` : "",
    inicio ? `inicio=${new Date(inicio).toISOString()}` : "",
    fim ? `fim=${new Date(fim + "T23:59:59").toISOString()}` : "",
  ].filter(Boolean).join("&");

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-5 float-in" data-testid="master-caixa">
      {/* CAIXAS ABERTOS */}
      <section data-testid="caixas-abertos-section">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div>
            <div className="chip mb-1.5 text-[10px] py-0.5" style={{ background: "rgba(16,185,129,0.18)", color: "#047857" }}>
              <span className="pulse-dot" /> Em aberto agora
            </div>
            <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Caixas Abertos</h1>
          </div>
          <button onClick={loadAbertos} data-testid="reload-abertos-btn"
                  className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5">
            <RefreshCw size={13} /> Atualizar
          </button>
        </div>

        {abertos.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <Kpi label="Caixas abertos" value={String(abertos.length)} />
            <Kpi label="Bruto parcial" value={formatBRL(consolidadoAbertos.bruto)} />
            <Kpi label="Líquido parcial" value={formatBRL(consolidadoAbertos.liquido)} />
            <Kpi label="Vendas" value={String(consolidadoAbertos.count)} />
          </div>
        )}

        {abertos.length === 0 ? (
          <div className="card-soft rounded-xl p-4 text-[13px] text-neutral-500" data-testid="abertos-empty">
            Nenhum caixa aberto no momento.
          </div>
        ) : (
          <div className="card-soft rounded-xl overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-emerald-50/60">
                  <TableHead className="text-[10px] uppercase tracking-widest">Vendedor</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest">Unidade</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest">Aberto há</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest">Bruto</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest">Líquido</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest">Vendas</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-widest text-right">Ação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {abertos.map((c) => (
                  <TableRow key={c.id} data-testid={`aberto-row-${c.id}`}>
                    <TableCell>
                      <div className="text-sm font-medium">{c.frentista_nome}</div>
                      <div className="text-[10px] text-neutral-500">{c.frentista_username}</div>
                    </TableCell>
                    <TableCell className="text-xs">{c.unit_nome}</TableCell>
                    <TableCell className="text-xs">
                      <span className="font-medium">{fmtAgo(c.aberto_em)}</span>
                      <div className="text-[10px] text-neutral-500">{new Date(c.aberto_em).toLocaleString("pt-BR")}</div>
                    </TableCell>
                    <TableCell>{formatBRL(c.total_bruto)}</TableCell>
                    <TableCell>{formatBRL(c.total_liquido)}</TableCell>
                    <TableCell>{c.count}</TableCell>
                    <TableCell className="text-right whitespace-nowrap space-x-1">
                      <button data-testid={`vendas-aberto-${c.id}`} onClick={() => setDetalhe({ caixa: c, podeExcluir: true })}
                              className="btn-ghost h-8 px-3 text-[11px] font-medium inline-flex items-center gap-1.5">
                        <Eye size={12} /> Vendas
                      </button>
                      <button data-testid={`fechar-aberto-${c.id}`} onClick={() => setConfirmFechar(c)}
                              className="btn-accent h-8 px-3 text-[11px] font-medium inline-flex items-center gap-1.5">
                        <Lock size={12} /> Fechar
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {/* CAIXAS FECHADOS */}
      <section data-testid="caixas-fechados-section">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <div>
            <div className="chip mb-1.5 text-[10px] py-0.5">Histórico</div>
            <h2 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Caixas Fechados</h2>
          </div>
          <ExcelBtn testid="excel-caixas" url={`/master/relatorios/caixas/excel${excelQuery ? "?" + excelQuery : ""}`}
                    filename="caixas_fechados.xlsx" label="Excel" />
        </div>

        <div className="card-soft rounded-xl p-4 grid md:grid-cols-4 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Vendedor</label>
            <select data-testid="filter-frentista" value={frentistaId} onChange={(e) => setFrentistaId(e.target.value)}
                    className={inputCls + " mt-1"}>
              <option value="">Todos</option>
              {frentistas.filter(f => unitId === "all" || f.unit_id === unitId).map((f) => (
                <option key={f.id} value={f.id}>{f.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Início</label>
            <input type="date" data-testid="filter-inicio" value={inicio} onChange={(e) => setInicio(e.target.value)}
                   className={inputCls + " mt-1"} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Fim</label>
            <input type="date" data-testid="filter-fim" value={fim} onChange={(e) => setFim(e.target.value)}
                   className={inputCls + " mt-1"} />
          </div>
          <div className="flex items-end">
            <button onClick={() => { setFrentistaId(""); setInicio(""); setFim(""); }}
                    className="btn-ghost h-10 w-full text-[12px] font-medium">
              Limpar Filtros
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <Kpi label="Bruto" value={formatBRL(consolidated.bruto)} />
          <Kpi label="Líquido" value={formatBRL(consolidated.liquido)} />
          <Kpi label="Descontos" value={formatBRL(consolidated.desconto)} accent />
          <Kpi label="Vendas" value={String(consolidated.count)} />
        </div>

        <div className="card-soft rounded-xl overflow-x-auto mt-3">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-[10px] uppercase tracking-widest">Abertura</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Fechamento</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Vendedor</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Tipo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Bruto</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Líquido</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Desc</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Vendas</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-right">Auditoria</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`caixa-row-${c.id}`}>
                  <TableCell className="text-xs">{new Date(c.aberto_em).toLocaleString("pt-BR")}</TableCell>
                  <TableCell className="text-xs">{c.fechado_em ? new Date(c.fechado_em).toLocaleString("pt-BR") : "—"}</TableCell>
                  <TableCell>{frentistas.find(f => f.id === c.frentista_id)?.nome || c.frentista_id?.slice(0, 8)}</TableCell>
                  <TableCell className="text-xs">{c.fechamento_tipo || "manual"}</TableCell>
                  <TableCell>{formatBRL(c.total_bruto)}</TableCell>
                  <TableCell>{formatBRL(c.total_liquido)}</TableCell>
                  <TableCell className="text-rose-600">{formatBRL(c.total_desconto)}</TableCell>
                  <TableCell>{c.count}</TableCell>
                  <TableCell className="text-right">
                    <button data-testid={`auditar-${c.id}`} onClick={() => setDetalhe({ caixa: c, podeExcluir: false })}
                            className="btn-ghost h-8 px-3 text-[11px] font-medium inline-flex items-center gap-1.5">
                      <FileSearch size={12} /> Auditar
                    </button>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-neutral-500 py-6">Nenhum caixa.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Detalhe / auditoria do caixa */}
      <Dialog open={!!detalhe} onOpenChange={(v) => !v && setDetalhe(null)}>
        <DialogContent className="max-w-2xl rounded-2xl border-none p-0 max-h-[90vh] overflow-hidden">
          <div className="bg-white p-5 rounded-2xl overflow-y-auto max-h-[90vh]">
            {detalhe && (
              <CaixaDetalhe caixa={detalhe.caixa} podeExcluir={detalhe.podeExcluir}
                            onChanged={() => { loadAbertos(); loadFechados(); }} />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirm dialog */}
      <AlertDialog open={!!confirmFechar} onOpenChange={(v) => !v && setConfirmFechar(null)}>
        <AlertDialogContent className="rounded-3xl border-none p-0 overflow-hidden max-w-[92vw] sm:max-w-md">
          <div className="glass p-5 rounded-3xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif-display text-2xl">Forçar fechamento?</AlertDialogTitle>
              <AlertDialogDescription className="text-neutral-600 text-[13px]">
                {confirmFechar && (
                  <>O caixa de <b>{confirmFechar.frentista_nome}</b> ({confirmFechar.unit_nome}) será fechado agora. O vendedor será desconectado da sessão atual no próximo acesso.</>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-4 gap-2">
              <AlertDialogCancel data-testid="confirm-master-fechar-cancel" className="rounded-full border-none btn-ghost h-11 px-5">Cancelar</AlertDialogCancel>
              <AlertDialogAction data-testid="confirm-master-fechar-ok" onClick={doFechar} className="btn-flow h-11 px-5 rounded-full">
                <Lock size={13} className="mr-1.5" /> Fechar caixa
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`card-soft rounded-xl p-3.5 ${accent ? "bg-gradient-to-br from-amber-50 to-orange-50" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-serif-display text-xl mt-0.5">{value}</div>
    </div>
  );
}

function CaixaDetalhe({ caixa, podeExcluir, onChanged }) {
  const [data, setData] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null); // atendimento a excluir
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await masterApi.get(`/master/caixas/${caixa.id}/detalhe`);
      setData(data);
    } catch (e) { modal.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [caixa.id]);

  const doExcluir = async () => {
    if (!confirmDel) return;
    setBusy(true);
    try {
      await masterApi.post(`/master/atendimentos/${confirmDel.id}/excluir`, { motivo });
      modal.success("Venda excluída e estornada");
      setConfirmDel(null); setMotivo("");
      await load();
      onChanged && onChanged();
    } catch (e) { modal.error(apiError(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <div className="text-sm text-neutral-500 py-6 text-center">Carregando…</div>;
  const atds = data.atendimentos || [];
  const excl = data.vendas_excluidas || [];

  return (
    <div className="space-y-3">
      <DialogHeader>
        <DialogTitle className="font-serif-display text-xl">
          {podeExcluir ? "Vendas do caixa (aberto)" : "Auditoria do caixa"}
        </DialogTitle>
      </DialogHeader>

      <div className="grid grid-cols-3 gap-2">
        <Kpi label="Bruto" value={formatBRL(data.total_bruto)} />
        <Kpi label="Líquido" value={formatBRL(data.total_liquido)} />
        <Kpi label="Descontos" value={formatBRL(data.total_desconto)} accent />
      </div>

      {Object.keys(data.descontos_por_tipo || {}).length > 0 && (
        <div className="card-soft rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1.5">Descontos por tipo</div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(data.descontos_por_tipo).map(([t, v]) => (
              <span key={t} className="chip text-[10px] py-0.5">{t}: {formatBRL(v)}</span>
            ))}
          </div>
        </div>
      )}

      <div className="card-soft rounded-xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-neutral-50">
            <TableHead className="text-[10px] uppercase tracking-widest">Hora</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Cliente</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Produto</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Litros</TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest">Líquido</TableHead>
            {podeExcluir && <TableHead className="text-[10px] uppercase tracking-widest text-right">Ação</TableHead>}
          </TableRow></TableHeader>
          <TableBody>
            {atds.map((a) => (
              <TableRow key={a.id} data-testid={`atd-row-${a.id}`}>
                <TableCell className="text-xs">{a.criado_em ? new Date(a.criado_em).toLocaleTimeString("pt-BR") : "—"}</TableCell>
                <TableCell className="text-xs">{a.cliente_nome}</TableCell>
                <TableCell className="text-xs">{a.produto}</TableCell>
                <TableCell className="text-xs">{(a.litros || 0).toFixed(2)}</TableCell>
                <TableCell className="text-xs">{formatBRL(a.valor_liquido)}</TableCell>
                {podeExcluir && (
                  <TableCell className="text-right">
                    <button data-testid={`excluir-venda-${a.id}`} onClick={() => { setConfirmDel(a); setMotivo(""); }}
                            className="icon-action icon-action--danger"><Trash2 size={13} /></button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {atds.length === 0 && <TableRow><TableCell colSpan={podeExcluir ? 6 : 5} className="text-center text-neutral-500 py-5">Nenhuma venda.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {excl.length > 0 && (
        <div className="card-soft rounded-xl p-3">
          <div className="text-[10px] uppercase tracking-widest text-rose-600 mb-1.5 flex items-center gap-1">
            <AlertTriangle size={11} /> Vendas excluídas (estornadas) — {excl.length}
          </div>
          <div className="space-y-1">
            {excl.map((e) => (
              <div key={e.id} className="text-[11px] text-neutral-600 flex justify-between gap-2 border-b border-neutral-100 pb-1">
                <span>{e.cliente_nome} · {formatBRL(e.valor_liquido)} · {e.produto}</span>
                <span className="text-neutral-400">{e.motivo || "sem motivo"} · {e.criado_em ? new Date(e.criado_em).toLocaleString("pt-BR") : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <AlertDialog open={!!confirmDel} onOpenChange={(v) => !v && setConfirmDel(null)}>
        <AlertDialogContent className="rounded-3xl border-none p-0 overflow-hidden max-w-[92vw] sm:max-w-md">
          <div className="glass p-5 rounded-3xl">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif-display text-2xl">Excluir venda?</AlertDialogTitle>
              <AlertDialogDescription className="text-neutral-600 text-[13px]">
                {confirmDel && (
                  <>A venda de <b>{confirmDel.cliente_nome}</b> ({formatBRL(confirmDel.valor_liquido)}) será excluída e seus
                  efeitos de fidelidade/cashback serão estornados. Esta ação fica registrada na auditoria.</>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="mt-3">
              <label className="text-[10px] uppercase tracking-widest text-neutral-500">Motivo (opcional)</label>
              <input data-testid="excluir-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)}
                     placeholder="Ex.: lançamento em duplicidade"
                     className="mt-1 w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-rose-500" />
            </div>
            <AlertDialogFooter className="mt-4 gap-2">
              <AlertDialogCancel data-testid="excluir-cancel" className="rounded-full border-none btn-ghost h-11 px-5">Cancelar</AlertDialogCancel>
              <AlertDialogAction data-testid="excluir-ok" disabled={busy} onClick={(e) => { e.preventDefault(); doExcluir(); }}
                                 className="btn-flow h-11 px-5 rounded-full !bg-rose-600">
                <Trash2 size={13} className="mr-1.5" /> {busy ? "Excluindo…" : "Excluir e estornar"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
